import './ProtocolEditorHeader.scss';

import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {connect, useDispatch, useSelector} from 'react-redux';
import {FmlxButton, FmlxIcon, FmlxTooltip} from 'fmlx-common-ui';
import {Button} from 'react-bootstrap';
import {getLastOpenedProtocol, selectOpenedProtocolIsEjectToCaddy} from 'stores/selectors/protocolSelectors';
import {Feedback, TaskType, TrayType} from 'constants/dataEnum';
import {getCards, getTasksByType} from 'stores/selectors/cardStateSelector';
import {getValidCards} from 'lib/cardAndTaskHelper';
import {
  selectHumidifierError,
  selectIsHumidifierConnected,
  selectIsHumidifierInstalled,
} from 'stores/selectors/humidifierSelectors';
import useHumidityWarnings from 'lib/hooks/useHumidityWarnings';
import {showSnackbar} from 'stores/actions/appStateAction';
import {useIntl} from 'react-intl';
import HeaderLayout from '../../../components/Layout/HeaderLayout';
import {DrawerButtonTitle} from '../../Common';
import OverlayHeader from '../../../components/OverlayHeader/OverlayHeader';
import withIntl from '../../../lib/withIntl';
import FastButton from '../../../components/FastButton';

// TODO: Refactor this redux state connect API to hooks API
function mapStateToProps(state) {
  return {
    protocolConfig: state.configState.protocolConfig,
    autosaveState: state.protocolEditorState.autosave,
    protocolEditorStateisPendingSaveExist: state.protocolEditorState.isPendingSaveExist,
    isADModalShown: state.virtualTrayState.aspirateDispenseVolumeModal.isShown,
    isVolumeModalOpen: state.protocolEditorState.volumeModalOpen,
  };
}

function ProtocolEditorHeader({
  title,
  onProtocolNameChange,
  intl,
  onStartClick,
  onTessaraSuperUserProtocolConvert,
  protocolConfig,
  disableUndo,
  disableRedo,
  disableStart,
  isTrayCompatible,
  onUndo,
  onRedo,
  headerRef,
  runOnly,
  autosaveState,
  onSaveChanges,
  protocolEditorStateisPendingSaveExist,
  isADModalShown,
  isVolumeModalOpen,
  isTessaraProtocol,
}) {
  const dispatch = useDispatch();
  const {formatMessage} = useIntl();
  const [showOverlayRunConfirmation, setShowOverlayRunConfirmation] = useState(false);
  const {trayType: protocolTrayType} = useSelector(getLastOpenedProtocol);
  const isHumidifierConnected = useSelector(selectIsHumidifierConnected);
  const isHumidifierInstalled = useSelector(selectIsHumidifierInstalled);
  const humidityTask = useSelector(getTasksByType(TaskType.ENABLE_HUMIDIFIER));
  const isProtocolHumidityEnabled = Boolean(humidityTask);
  const cards = useSelector(getCards);
  const isEjectToCaddy = useSelector(selectOpenedProtocolIsEjectToCaddy);
  const humidifierError = useSelector(selectHumidifierError);
  const checkHumidityWarnings = useHumidityWarnings();

  const requiredTips = useMemo(
    () => getValidCards(Object.values(cards)).reduce((acc, card) => acc + (card.requiredTips || 0), 0),
    [cards],
  );

  const handleClickStart = async () => {
    if (isADModalShown || isVolumeModalOpen) {
      return;
    }

    const humidityOk = await checkHumidityWarnings();
    if (!humidityOk) return;

    if (isHumidifierInstalled && isProtocolHumidityEnabled) {
      if (humidifierError) return;

      if (!isHumidifierConnected) {
        dispatch(
          showSnackbar(
            Feedback.ERROR,
            formatMessage({id: 'protocolEditor.toast.humidifierIsDisconnected.description'}),
            null,
            true,
            true,
            null,
            '',
            formatMessage({id: 'protocolEditor.toast.humidifierIsDisconnected.title'}),
          ),
        );
        return;
      }
    }

    setShowOverlayRunConfirmation(true);
  };

  function renderRunConfirmationOverlay() {
    let caddyTips = 384;
    if (protocolTrayType === TrayType.DOUBLE_CADDY && !isEjectToCaddy) {
      caddyTips = 768;
    } else if (protocolTrayType === TrayType.HIGH_VOLUME_CADDY) {
      caddyTips = 192;
    }

    const getProtocolStartMessage = () => {
      let message = 'protocolEditor.runConfirmation';
      if (requiredTips > caddyTips) {
        if (isEjectToCaddy) {
          message = 'protocolEditor.moreThanFullCaddy.ejectTipsToCaddy.runConfirmation';
        } else {
          message = 'protocolEditor.moreThanFullCaddy.runConfirmation';
        }
      } else if (isEjectToCaddy) {
        message = 'protocolEditor.ejectTipsToCaddy.runConfirmation';
      }
      return message;
    };

    return (
      <OverlayHeader>
        <OverlayHeader.Left>
          <span className="overlay-text">
            {intl.formatMessage(
              {
                id: getProtocolStartMessage(),
              },
              {caddyTips, requiredTips},
            )}
          </span>
        </OverlayHeader.Left>
        <OverlayHeader.Right>
          <Button
            style={{marginRight: '5px', width: '85px'}}
            onClick={() => {
              onStartClick();
              setShowOverlayRunConfirmation(false);
            }}
          >
            {intl.formatMessage({id: 'protocolEditor.button.go'})}
          </Button>
          <Button
            className="button-back"
            bsStyle="warning"
            style={{marginRight: '12px', width: '85px'}}
            onClick={() => setShowOverlayRunConfirmation(false)}
          >
            {intl.formatMessage({id: 'protocolEditor.button.back'})}
          </Button>
        </OverlayHeader.Right>
      </OverlayHeader>
    );
  }

  return (
    <HeaderLayout>
      <HeaderLayout.Left className="protocol-editor-header__left">
        <DrawerButtonTitle
          title={title}
          editable={!runOnly}
          clickable
          maxCharacters={protocolConfig.nameMaxLength}
          showCharactersLeft
          onChange={onProtocolNameChange}
        />
        {!(autosaveState || runOnly) && (
          <FmlxTooltip title={intl.formatMessage({id: 'protocolEditor.button.save.tooltip'})}>
            <span>
              <FmlxButton
                withIcon="start"
                icon={<FmlxIcon name="Save" fontSize="large" />}
                label={intl.formatMessage({id: 'protocolEditor.button.save'})}
                onClick={protocolEditorStateisPendingSaveExist ? onSaveChanges : () => {}}
                disabled={!protocolEditorStateisPendingSaveExist}
              />
            </span>
          </FmlxTooltip>
        )}
      </HeaderLayout.Left>
      <HeaderLayout.Right>
        <div ref={headerRef} className="protocol-editor-header__right">
          {onTessaraSuperUserProtocolConvert && (
            <FastButton
              fitContainer
              variant="outline"
              onClick={() => onTessaraSuperUserProtocolConvert(!isTessaraProtocol)}
              label={intl.formatMessage({
                id: isTessaraProtocol
                  ? 'protocolEditor.button.convertToRegularProtocol'
                  : 'protocolEditor.button.convertToTessaraProtocol',
              })}
              height="60%"
              withIcon="start"
              icon={<FmlxIcon name={`${isTessaraProtocol ? 'Protocol' : 'FastTessara'}`} />}
            />
          )}
          {!runOnly && (
            <Button bsStyle="warning" disabled={disableUndo} onClick={onUndo}>
              <span className="icon-undo" />
            </Button>
          )}
          {!runOnly && (
            <Button bsStyle="warning" disabled={disableRedo} onClick={onRedo}>
              <span className="icon-redo" />
            </Button>
          )}
          <FmlxTooltip title={!isTrayCompatible ? intl.formatMessage({id: 'protocolEditor.button.start.tooltip'}) : ''}>
            <span>
              <FmlxButton
                label={intl.formatMessage({id: 'protocolEditor.button.start'})}
                type="positive"
                disabled={disableStart}
                onClick={handleClickStart}
              />
            </span>
          </FmlxTooltip>
        </div>
      </HeaderLayout.Right>
      {showOverlayRunConfirmation && renderRunConfirmationOverlay()}
    </HeaderLayout>
  );
}

ProtocolEditorHeader.propTypes = {
  title: PropTypes.string,
  onProtocolNameChange: PropTypes.func,
  intl: PropTypes.any.isRequired,
  onStartClick: PropTypes.func,
  protocolConfig: PropTypes.object.isRequired,
  disableUndo: PropTypes.bool,
  disableRedo: PropTypes.bool,
  disableStart: PropTypes.bool,
  isTrayCompatible: PropTypes.bool,
  onUndo: PropTypes.func,
  onRedo: PropTypes.func,
  headerRef: PropTypes.object,
  runOnly: PropTypes.bool.isRequired,
  autosaveState: PropTypes.bool.isRequired,
  onSaveChanges: PropTypes.func.isRequired,
  onTessaraSuperUserProtocolConvert: PropTypes.func,
  protocolEditorStateisPendingSaveExist: PropTypes.bool.isRequired,
  isADModalShown: PropTypes.bool.isRequired,
  isVolumeModalOpen: PropTypes.bool.isRequired,
  isTessaraProtocol: PropTypes.bool.isRequired,
};

ProtocolEditorHeader.defaultProps = {
  title: 'New Protocol 1',
  onProtocolNameChange: () => {},
  onStartClick: () => {},
  onTessaraSuperUserProtocolConvert: null,
  disableUndo: true,
  disableRedo: true,
  disableStart: true,
  isTrayCompatible: true,
  onUndo: () => {},
  onRedo: () => {},
  headerRef: null,
};

function areEqual(prevProps, nextProps) {
  return (
    prevProps.title === nextProps.title &&
    prevProps.disableUndo === nextProps.disableUndo &&
    prevProps.disableRedo === nextProps.disableRedo &&
    prevProps.disableStart === nextProps.disableStart &&
    prevProps.onTessaraSuperUserProtocolConvert === nextProps.onTessaraSuperUserProtocolConvert &&
    prevProps.isTessaraProtocol === nextProps.isTessaraProtocol
  );
}

export default React.memo(connect(mapStateToProps)(withIntl(ProtocolEditorHeader)), areEqual);
